import clsx from 'clsx';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDidMount } from 'rooks';
import { sqWorksheetStore } from '@/core/core.stores';
import { TIME_FORMAT_NUMERICAL_SHORT, formatTime } from '@/datetime/dateTime.utilities';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { getWorkbook } from '@/utilities/homescreen.utilities';
import { setOpeningAndLoadingItemId } from '@/workbench/workbench.actions';
import { WorksheetThumbnail } from '@/worksheets/WorksheetThumbnail.atom';
import { Icon } from '@seeqdev/qomponents';
import type { StoreWorkbookWithWorksheets } from '@/worksheet/worksheet.types';

interface SelectWorksheetProps {
  workbookId: string;
  worksheetIds: string[];
  workstepIds: string[];
  setWorksheetIds: (ids: string[]) => void;
  setWorkstepIds: (ids: string[]) => void;
  setTotalWorksheets: (totalWorksheets: number) => void;
  isLoading?: boolean;
  setParentWorkbook?: (workbook: StoreWorkbookWithWorksheets) => void;
  disableAutoSelectFirst?: boolean;
  worksheetFilter?: 'all' | 'documents' | 'dashboards';
}

export const SelectWorksheet = (props: SelectWorksheetProps) => {
  const {
    isLoading = false,
    workbookId,
    worksheetIds,
    workstepIds,
    setWorksheetIds,
    setWorkstepIds,
    setTotalWorksheets,
    setParentWorkbook,
    disableAutoSelectFirst = false,
    worksheetFilter = 'all',
  } = props;

  const [worksheets, setWorksheets] = useState<React.JSX.Element[]>([]);
  const [workbook, setWorkbook] = useState<StoreWorkbookWithWorksheets | undefined>(undefined);

  const setSelectedWorksheets = useCallback(
    (worksheetId: string, workstepId: string, totalWorksheets: number) => {
      _.includes(worksheetIds, worksheetId)
        ? setWorksheetIds(_.without(worksheetIds, worksheetId))
        : setWorksheetIds(worksheetIds.concat(worksheetId));
      _.includes(workstepIds, workstepId)
        ? setWorkstepIds(_.without(workstepIds, workstepId))
        : setWorkstepIds(workstepIds.concat(workstepId));
      setTotalWorksheets(totalWorksheets);
    },
    [setTotalWorksheets, setWorksheetIds, setWorkstepIds, worksheetIds, workstepIds],
  );

  useDidMount(() => {
    getWorkbook(workbookId, { includeWorkstepId: true })
      .then((workbook) => {
        if (
          !disableAutoSelectFirst &&
          (_.isEmpty(worksheetIds) || _.first(worksheetIds) === undefined) &&
          workbook?.worksheets?.length === 1
        ) {
          const worksheet = _.first(workbook.worksheets) as any;
          setSelectedWorksheets(worksheet.worksheetId, worksheet.workstepId, 1);
        }
        setWorkbook(workbook);
        setParentWorkbook && setParentWorkbook(workbook);
      })
      .finally(() => setOpeningAndLoadingItemId(null));
  });

  useEffect(() => {
    if (!workbook) return;
    const isVantage = workbook.type === SeeqNames.Types.Vantage;

    const filteredWorksheetList = workbook.worksheets?.filter((worksheet) => {
      switch (worksheetFilter) {
        case 'documents':
          return worksheet.isDashboard !== true;
        case 'dashboards':
          return worksheet.isDashboard === true;
        default:
          return true;
      }
    });

    setWorksheets(
      _.map(filteredWorksheetList, (worksheet) => (
        <div
          data-testid={`${worksheet.worksheetId}_selectWorksheet`}
          key={worksheet.worksheetId}
          className={clsx(
            'worksheetContainer cursorPointer text-center mr15 mb15',
            _.includes(worksheetIds, worksheet.worksheetId) ? 'active-worksheet' : '',
          )}
          onClick={() =>
            setSelectedWorksheets(
              worksheet.worksheetId as string,
              worksheet.workstepId as string,
              filteredWorksheetList.length,
            )
          }
          id={worksheet.worksheetId}>
          {!isVantage && (
            <WorksheetThumbnail
              workbookId={workbookId}
              worksheetId={worksheet.worksheetId as string}
              isActive={false}
            />
          )}
          <div className={isVantage ? '' : 'mt5'}>
            {`${worksheet.name}`}
            <br />
            {`(${formatTime(worksheet.updatedAt, sqWorksheetStore.timezone, TIME_FORMAT_NUMERICAL_SHORT)})`}
          </div>
        </div>
      )),
    );
  }, [setSelectedWorksheets, workbook, workbookId, worksheetIds, workstepIds, worksheetFilter]);

  return (
    <div className="flexRowContainer" data-testid="report-content-worksheet">
      <div className="report-worksheets-list flexColumnContainer flexFill flexWrap flexCenter">
        {_.isEmpty(worksheets) || isLoading ? (
          <Icon
            icon="fa-spinner"
            large={true}
            iconPrefix="fa-solid"
            extraClassNames="sq-fairly-dark-gray fa-spin-pulse"
          />
        ) : (
          worksheets
        )}
      </div>
    </div>
  );
};
